import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCulturalEventsAPI } from '../hooks/useCulturalEventsAPI';
import { CulturalEvent } from '../constants/culturalEventsTypes';
import { LoadingSpinner } from '../Shared/LoadingSpinner';
import { ErrorMessage } from '../Shared/ErrorMessage';
import { useBrownBlueSpeckleBackground } from '../hooks/useBrownBlueSpeckleBackground';
import { useTigerLilyAnimation } from '../hooks/useTigerLilyAnimation';
import './EventDetailPage.css';

export const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getEventById } = useCulturalEventsAPI();
  const [event, setEvent] = useState<CulturalEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);
  const heroCanvasRef = useBrownBlueSpeckleBackground(heroRef);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentCanvasRef = useTigerLilyAnimation(contentRef as React.RefObject<HTMLDivElement>);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await getEventById(id);
        setEvent(data.event || data);
      } catch (err: any) {
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, getEventById]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <div className="event-detail-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-error">
        <ErrorMessage message={error || 'Event not found'} />
        <button className="back-btn" onClick={() => navigate('/cultural-events')}>
          ← Back to Events
        </button>
      </div>
    );
  }

  return (
    <div className="event-detail-page">
      {/* Header */}
      <div className="event-detail-header">
        <div className="event-detail-container">
          <button className="back-btn" onClick={() => navigate('/cultural-events')}>
            ← Back to Events
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="event-detail-hero" ref={heroRef}>
        <canvas 
          ref={heroCanvasRef} 
          id="speckle-canvas-event-detail"
          className="speckle-canvas"
        />
        <div className="event-detail-container">
          <div className="hero-badges">
            <span className="event-type-badge">{event.eventType}</span>
          </div>
          <h1 className="hero-title">{event.title}</h1>
          <div className="hero-meta">
            <div className="meta-item">
              <span className="icon">📅</span>
              {formatDate(event.date)}
            </div>
            <div className="meta-item">
              <span className="icon">🕐</span>
              {formatTime(event.time)}
            </div>
            <div className="meta-item">
              <span className="icon">📍</span>
              {event.location}
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="event-detail-content" ref={contentRef as any}>
        <canvas 
          ref={contentCanvasRef} 
          id="tiger-lily-canvas-event-detail-content"
          className="tiger-lily-canvas"
        />
        <div className="event-detail-container">
          <div className="content-grid">
            {/* Main Content */}
            <div className="content-main">
              <div className="content-section">
                <h2>About This Event</h2>
                <p className="event-description">{event.description}</p>
                {event.detailedDescription && (
                  <p className="event-detailed-description">{event.detailedDescription}</p>
                )}
              </div>

              <div className="content-section">
                <h2>Location</h2>
                <p className="location-name">{event.location}</p>
                {event.venue && <p className="location-venue">{event.venue}</p>}
                {event.address && <p className="location-address">{event.address}</p>}
              </div>
            </div>

            {/* Sidebar */}
            <div className="content-sidebar">
              <div className="booking-card">
                <div className="booking-price">
                  {event.isFree ? (
                    <span className="price-free">Free</span>
                  ) : (
                    <>
                      <span className="price-currency">£</span>
                      <span className="price-amount">{event.price}</span>
                    </>
                  )}
                </div>

                {event.maxAttendees && (
                  <p className="booking-info">
                    Limited to {event.maxAttendees} attendees
                  </p>
                )}

                {event.paymentLink ? (
                  <a 
                    href={event.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="booking-btn"
                  >
                    Book Now
                  </a>
                ) : (
                  <button className="booking-btn" disabled>
                    Registration Closed
                  </button>
                )}

                <p className="booking-note">
                  Secure payment via Stripe
                </p>
              </div>

              <div className="info-card">
                <h3>Event Details</h3>
                <div className="info-item">
                  <strong>Date:</strong>
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="info-item">
                  <strong>Time:</strong>
                  <span>{formatTime(event.time)}</span>
                </div>
                <div className="info-item">
                  <strong>Type:</strong>
                  <span style={{ textTransform: 'capitalize' }}>{event.eventType}</span>
                </div>
                {event.currency && (
                  <div className="info-item">
                    <strong>Currency:</strong>
                    <span>{event.currency}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

